<template>
  <v-card class="mx-auto my-4 rounded-lg" elevation="2" max-width="700">
    <!-- Header -->
    <v-sheet color="primary" class="pa-4 d-flex align-center rounded-t-lg">
      <v-icon color="white" large class="mx-3"
        >mdi-file-document-edit-outline</v-icon
      >
      <div>
        <h2 class="text-h6 white--text font-weight-bold mb-0">
          דוח מצב למועצה
        </h2>
        <div class="white--text text-caption" style="opacity: 0.9">
          {{ currentCategoryText }}
        </div>
      </div>
    </v-sheet>

    <v-card-text class="pt-6 px-6">
      <v-form @submit.prevent="sendReport">
        <v-textarea
          v-model="reportContent"
          outlined
          auto-grow
          rows="4"
          label="תוכן הדיווח"
          placeholder="כתוב כאן את פרטי הדיווח..."
          hide-details="auto"
          class="rounded-lg mb-2"
          color="primary"
          background-color="grey lighten-5"
        >
          <template v-slot:prepend-inner>
            <v-icon color="primary" class="mt-1">mdi-pencil</v-icon>
          </template>
        </v-textarea>

        <div class="d-flex justify-end mt-4">
          <v-btn
            type="submit"
            color="primary"
            large
            elevation="2"
            :loading="sending"
            :disabled="!hasContent"
            class="px-8 rounded-pill font-weight-bold"
          >
            שלח דיווח
            <v-icon right style="transform: scaleX(-1)">mdi-send</v-icon>
          </v-btn>
        </div>
      </v-form>
    </v-card-text>

    <!-- Last Report Section -->
    <v-expand-transition>
      <div v-if="lastReport">
        <v-divider></v-divider>
        <div class="grey lighten-5 pa-6 rounded-b-lg">
          <div class="d-flex align-center mb-3">
            <v-icon small color="grey darken-1" class="mx-2"
              >mdi-history</v-icon
            >
            <span
              class="text-subtitle-2 grey--text text--darken-2 font-weight-bold"
              >דיווח אחרון</span
            >
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">
              {{ formatDate(lastReport.createdAt) }}
            </span>
          </div>

          <v-card
            outlined
            class="rounded-lg white pa-0 overflow-hidden border-card"
          >
            <div class="pa-4">
              <div class="body-1 text--primary" style="white-space: pre-wrap">
                {{ lastReport.content }}
              </div>
            </div>

            <!-- Response Actions -->
            <div
              class="pa-3 grey lighten-5 border-top-light d-flex justify-space-between align-center"
            >
              <div
                v-if="!lastReport.response"
                class="text-caption grey--text text--darken-1 px-2"
              >
                <v-icon x-small class="mx-1">mdi-clock-outline</v-icon>
                ממתין להתייחסות המועצה
              </div>
              <v-spacer v-if="!lastReport.response"></v-spacer>

              <v-chip
                v-if="lastReport.response"
                color="success"
                class="px-4 font-weight-bold"
                @click="showResponse = true"
                style="cursor: pointer"
              >
                <v-icon left small>mdi-message-reply-text</v-icon>
                תגובה מהמועצה
              </v-chip>
            </div>
          </v-card>
        </div>
      </div>
    </v-expand-transition>

    <!-- Response Drawer -->
    <v-bottom-sheet v-model="showResponse" inset max-width="700">
      <v-sheet class="text-center pa-6 rounded-t-xl">
        <div class="d-flex justify-center mb-4">
          <v-avatar color="success lighten-5" size="60">
            <v-icon color="success" large>mdi-message-reply-text</v-icon>
          </v-avatar>
        </div>
        <div class="text-h5 mb-2 primary--text font-weight-bold">
          תגובת המועצה
        </div>
        <v-divider class="mb-4 mx-auto" style="max-width: 100px"></v-divider>

        <div
          class="text-h6 font-weight-regular mb-6 px-4"
          v-if="lastReport && lastReport.response"
          style="white-space: pre-wrap; line-height: 1.6"
        >
          "{{ lastReport.response }}"
        </div>

        <div
          class="caption grey--text mb-6"
          v-if="lastReport && lastReport.responseBy"
        >
          <v-icon x-small class="mx-1">mdi-account</v-icon>
          ע"י {{ lastReport.responseBy }}
          <span class="mx-1">•</span>
          <v-icon x-small class="mx-1">mdi-clock-outline</v-icon>
          {{ formatDate(lastReport.responseTime) }}
        </div>

        <v-btn
          outlined
          color="primary"
          rounded
          large
          class="px-8"
          @click="showResponse = false"
        >
          סגור
        </v-btn>
      </v-sheet>
    </v-bottom-sheet>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="3000"
      top
      rounded="pill"
    >
      <div class="d-flex align-center">
        <v-icon color="white" class="mx-2">{{ snackbarIcon }}</v-icon>
        {{ snackbarText }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">סגור</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import { categories } from '@/helpers/categoriesAndTypes'
import myAxios from '@/services/myAxios'

export default {
  name: 'CouncilReport',
  props: {
    activeCategory: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      reportContent: '',
      sending: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      lastReport: null,
      showResponse: false,
    }
  },
  sockets: {
    'council-report-response-received'(data) {
      if (
        this.lastReport &&
        this.lastReport.teamName === data.teamName &&
        this.lastReport.department === data.department
      ) {
        this.lastReport = {
          ...this.lastReport,
          response: data.response,
          responseBy: data.responseBy,
          responseTime: data.responseTime || new Date(),
        }
        this.showResponse = true
        this.showSnackbar('התקבלה תגובה חדשה מהמועצה!', 'info')
      }
    },
  },
  computed: {
    currentCategoryText() {
      const category = categories.find(
        cat => cat.category === this.activeCategory
      )
      if (this.activeCategory === 'headQuaters') {
        return 'מטה - תמונת מצב יישובית'
      }
      return category?.text || this.activeCategory
    },
    hasContent() {
      return this.reportContent && this.reportContent.trim().length > 0
    },
    user() {
      return this.$store.getters.user
    },
    team() {
      return this.$store.getters.team
    },
    snackbarIcon() {
      switch (this.snackbarColor) {
        case 'success':
          return 'mdi-check-circle'
        case 'error':
          return 'mdi-alert-circle'
        case 'info':
          return 'mdi-information'
        default:
          return 'mdi-bell'
      }
    },
  },
  watch: {
    activeCategory: {
      immediate: true,
      handler() {
        this.fetchLastReport()
      },
    },
  },
  methods: {
    async fetchLastReport() {
      if (!this.team || !this.activeCategory) return

      try {
        const response = await myAxios.get('/council-report/single', {
          params: {
            teamName: this.team,
            department: this.activeCategory,
          },
        })
        this.lastReport = response.data

        if (this.lastReport && this.lastReport.content) {
          this.reportContent = this.lastReport.content
        } else {
          this.reportContent = ''
        }
      } catch (error) {
        console.error('Error fetching last report:', error)
      }
    },
    async sendReport() {
      if (!this.hasContent) {
        this.showSnackbar('אנא מלא את תוכן הדוח', 'error')
        return
      }

      this.sending = true

      try {
        const reportPayload = {
          teamName: this.$store.getters.team,
          category: this.activeCategory,
          content: this.reportContent,
          username: this.$store.getters.username,
          timestamp: new Date().toISOString(),
        }

        await this.$store.dispatch('socket/councilReport/send', reportPayload)

        // Optimistically update last report
        this.lastReport = {
          ...reportPayload,
          department: reportPayload.category,
          createdAt: reportPayload.timestamp,
          response: null,
          responseBy: null,
          responseTime: null,
        }

        this.showSnackbar('הדוח נשלח בהצלחה למועצה!', 'success')
      } catch (error) {
        console.error('Error sending council report:', error)
        this.showSnackbar('שגיאה בשליחת הדוח', 'error')
      } finally {
        this.sending = false
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('he-IL', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    },
  },
}
</script>

<style scoped>
.border-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.border-top-success {
  border-top: 1px solid rgba(76, 175, 80, 0.2);
}
</style>
